/**
 * Centralized principal-KIND metadata for the one registry (agent | workload_identity | api_key, and a
 * person's own device identity, which the roster excludes but a single read can return).
 *
 * One source of truth for how each kind is presented (icon + label + one-line description) so the
 * roster, the detail page, and any kind filter can't drift. Kind is encoded by SHAPE (a distinct icon
 * per kind) as well as label — the roster mixes all three kinds, so an operator must be able to tell a
 * workload identity from an agent at a glance. Sentence case; "API" is an allowlisted acronym.
 */

import { Bot, KeySquare, MonitorSmartphone, Server, type LucideIcon } from "lucide-react";
import { Badge, cn } from "@pureid/ui";
import type { Agent } from "./schemas";

/** Every kind the registry returns; the create and filter forms offer only the non-human three. */
type RegistryKind = Agent["kind"];

interface KindMeta {
  label: string;
  // Mid-sentence noun form. NOT `label.toLowerCase()` — that would mangle the allowlisted acronym
  // "API key" into "api key". Used where the kind name appears inside prose.
  lowerLabel: string;
  icon: LucideIcon;
  description: string;
}

export const KIND_META: Record<RegistryKind, KindMeta> = {
  agent: {
    // "AI agent" (not bare "Agent") disambiguates for a cold operator — a support/user/service agent is
    // something else — and aligns with the "AI Agent Governance" area name. PM+CMO label review, 2026-07.
    label: "AI agent",
    lowerLabel: "AI agent",
    icon: Bot,
    description: "An AI agent acting on a person's behalf under delegated, bounded authority.",
  },
  workload_identity: {
    label: "Workload identity",
    lowerLabel: "workload identity",
    icon: Server,
    description:
      "A non-human workload governed here, authenticating with a federated workload identity.",
  },
  api_key: {
    // "API-key integration" (distinct label + KeySquare icon) so this NHI registry kind never collides
    // with the SEPARATE personal admin-API keys surface (/api-keys, "API keys" + KeyRound). "integration"
    // (not the IAM-jargon "principal") is the plain-voice term for a partner/script that connects with a
    // static key — PM+CMO label review, 2026-07. Deferred stub today (no create path).
    label: "API-key integration",
    lowerLabel: "API-key integration",
    icon: KeySquare,
    description: "A read-only, key-based integration governed under the same ceiling.",
  },
  self: {
    // A person's own device identity. It lives under People; the registry returns it only on a
    // direct read, so it needs a name here rather than a missing entry.
    label: "Device identity",
    lowerLabel: "device identity",
    icon: MonitorSmartphone,
    description: "A person's own device, governed under People.",
  },
};

/** AgentKindIcon renders just the kind's icon (for the name cell / headings). */
export function AgentKindIcon({
  kind,
  className,
}: {
  kind: RegistryKind;
  className?: string;
}) {
  const Icon = KIND_META[kind].icon;
  return <Icon className={cn("h-4 w-4", className)} aria-hidden />;
}

/** AgentKindBadge renders the kind as an icon + label chip. */
export function AgentKindBadge({ kind, className }: { kind: RegistryKind; className?: string }) {
  const { label, icon: Icon } = KIND_META[kind];
  return (
    <Badge variant="outline" className={cn("gap-1 font-normal", className)}>
      <Icon className="h-3 w-3" aria-hidden />
      {label}
    </Badge>
  );
}
